import { useId, useRef, useState, useMemo, useEffect } from 'react';
import { errMsg } from '../../lib/errors';
import { ConfirmButton } from '../ui/ConfirmButton';
import {
  ArrowLeft,
  Save,
  Plus,
  Pause,
  Play,
  Trash2,
  Layers,
  ListChecks,
  Check,
  AlertCircle,
  Search,
  Trophy,
  ChevronRight,
} from 'lucide-react';
import {
  TOURNAMENT_TYPES,
  getTournamentType,
  getTournamentWinners,
  getDisplayTournamentId,
} from '../../engine';
import { highlightMatch } from './highlightMatch';
import { AllMatchesArchive } from './AllMatchesArchive';
import { useT } from '../../i18n';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';

// ─────────────────────────────────────────────────────────────────────
// ArchiveView — "Turneringsarkiv" top-level view: the "Turneringer"
// (activities) tab with search/type/status filters, bulk select + export +
// import + delete, pagination, and the paused-tournaments banner; plus the
// "Alle kamper" tab which delegates to AllMatchesArchive. Extracted verbatim
// from the original monolith (legacy/App.jsx).
//
// Engine swaps (monolith → src/engine named imports, same functions):
//   Padel24Engine.getTournamentType → getTournamentType
//   getTournamentWinners / getDisplayTournamentId → '../../engine'
//   TOURNAMENT_TYPES → '../../engine'
//   highlightMatch → './highlightMatch'
//   AllMatchesArchive → './AllMatchesArchive'
// getTournamentType is the PURE engine fn (in src/engine); the live
// Padel24Engine object is not needed, so no engine prop is taken.
//
// Forced type deviation (the original monolith was @ts-nocheck, so tsc never
// saw this):
// in handleImport, FileReader's `e.target.result` is typed string | ArrayBuffer,
// so JSON.parse needs `result as string` (same precedent as ClubSettingsView).
// Runtime is unchanged.
// ─────────────────────────────────────────────────────────────────────

export interface ArchiveViewProps {
  archive: any[];
  effectivePlayerDB?: any;
  onBack: () => void;
  onOpenArchived: (data: any) => void;
  onDeleteArchived: (key: any) => void;
  /** How many entries landed and how many did not (#1329). */
  onImport?: (
    data: any,
  ) => Promise<{ imported: number; failed: number }> | { imported: number; failed: number };
  onPlayerClick?: (p: any) => void;
  pausedTournaments?: any[];
  onResumePaused?: (id: any) => void;
  onDeletePaused?: (id: any) => void;
}

export function ArchiveView({
  archive,
  effectivePlayerDB = {},
  onBack,
  onOpenArchived,
  onDeleteArchived,
  onImport,
  onPlayerClick,
  pausedTournaments = [],
  onResumePaused,
  onDeletePaused,
}: ArchiveViewProps) {
  const [viewMode, setViewMode] = useState('activities'); // 'activities' | 'matches'
  const [selected, setSelected] = useState(new Set());
  // `tr`, not `t`: this view's rows already call each tournament `t`.
  const tr = useT();
  const [confirmDelete, setConfirmDelete] = useState(null);
  const uid = safeId('archive', useId());
  const confirmIds = { title: `${uid}-confirm-title`, body: `${uid}-confirm-body` };
  // Focus starts on «Avbryt»: the other button deletes permanently.
  const cancelDeleteRef = useRef<HTMLButtonElement | null>(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [importStatus, setImportStatus] = useState(null); // 'success' | 'error' | null
  const [importMessage, setImportMessage] = useState('');

  // Export: download JSON with all (or selected) tournaments
  const handleExport = (onlySelected = false) => {
    const toExport = onlySelected
      ? archive.filter((item) => selected.has(item.key)).map((item) => item.data)
      : archive.map((item) => item.data);

    if (toExport.length === 0) {
      setImportStatus('error');
      setImportMessage('Ingen turneringer å eksportere');
      setTimeout(() => setImportStatus(null), 3000);
      return;
    }

    const json = JSON.stringify(toExport, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const date = new Date().toISOString().slice(0, 10);
    a.download = `padel24-backup-${date}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setImportStatus('success');
    setImportMessage(
      `Eksporterte ${toExport.length} turnering${toExport.length === 1 ? '' : 'er'}`,
    );
    setTimeout(() => setImportStatus(null), 3000);
  };

  // Import: read JSON file and add tournaments
  const handleImport = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = JSON.parse(e.target.result as string);
        if (!onImport) {
          throw new Error('Import-funksjon ikke tilgjengelig');
        }
        const { imported: count, failed } = await onImport(data);
        // NO SUCCESS LINE OVER A PARTIAL IMPORT (#1329): the store's toast
        // already says how many did not land, and «Importerte N» beside it
        // reads as «done».
        if (failed > 0) return;
        setImportStatus('success');
        setImportMessage(`Importerte ${count} turnering${count === 1 ? '' : 'er'}`);
        setTimeout(() => setImportStatus(null), 4000);
      } catch (err) {
        setImportStatus('error');
        setImportMessage(`Import feilet: ${errMsg(err) || 'Ukjent feil'}`);
        setTimeout(() => setImportStatus(null), 5000);
      }
    };
    reader.onerror = () => {
      setImportStatus('error');
      setImportMessage('Kunne ikke lese filen');
      setTimeout(() => setImportStatus(null), 5000);
    };
    reader.readAsText(file);

    // Reset the input so the same file can be selected again
    event.target.value = '';
  };

  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'done' | 'saved'
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);

  // Type-filter buttons — derived from the archive so the filter always
  // matches which tournament formats actually exist (P24, KM, the mini types,
  // EK). Same pattern as AllMatchesArchive's availableTypes. Order follows
  // TOURNAMENT_TYPES; unknown types end up last.
  const availableTypes = useMemo(() => {
    const present = new Set(
      archive
        .filter((item) => !item.data?.matchesOnly)
        .map((item) => item.data.config?.tournamentType || item.data.type || 'padel24'),
    );
    const opts = [{ id: 'all', label: 'Alle typer' }];
    Object.keys(TOURNAMENT_TYPES).forEach((id) => {
      if (present.has(id)) {
        const info = TOURNAMENT_TYPES[id];
        opts.push({ id, label: info.label || info.abbrev });
      }
    });
    present.forEach((t) => {
      if (!TOURNAMENT_TYPES[t]) opts.push({ id: t, label: t });
    });
    return opts;
  }, [archive]);

  // Status bucket for an archive entry: 'done' (Ferdig) or 'saved' (Lagret).
  // Matches the status badge on the rows (t.status === 'done' => Ferdig, otherwise Lagret).
  const statusKey = (data) => (data?.status === 'done' || data?.isSeriesSeason ? 'done' : 'saved');

  // Statuses available in the dataset (for the status dropdown)
  const availableStatuses = useMemo(() => {
    const present = new Set(
      archive
        .filter((item) => !item.data?.matchesOnly && !item.data?.isVirtual)
        .map((item) => statusKey(item.data)),
    );
    const opts = [{ id: 'all', label: 'Alle statuser' }];
    if (present.has('done')) opts.push({ id: 'done', label: 'Ferdig' });
    if (present.has('saved')) opts.push({ id: 'saved', label: 'Lagret' });
    return opts;
  }, [archive]);

  const filteredArchive = useMemo(() => {
    let list = archive.filter((item) => !item.data?.matchesOnly); // matchesOnly only shows in «Kamper»

    if (statusFilter !== 'all') {
      list = list.filter((item) =>
        item.data?.isVirtual ? false : statusKey(item.data) === statusFilter,
      );
    }

    if (typeFilter !== 'all') {
      list = list.filter((item) => {
        const t = item.data.config?.tournamentType || item.data.type || 'padel24';
        return t === typeFilter;
      });
    }

    if (search.trim()) {
      const q = search.toLowerCase().trim();
      list = list.filter((item) => (item.data.config?.name || '').toLowerCase().includes(q));
    }

    list.sort((a, b) => {
      if (sortBy === 'newest') return (b.data.archivedAt || 0) - (a.data.archivedAt || 0);
      if (sortBy === 'oldest') return (a.data.archivedAt || 0) - (b.data.archivedAt || 0);
      if (sortBy === 'name')
        return (a.data.config?.name || '').localeCompare(b.data.config?.name || '', 'nb');
      if (sortBy === 'players')
        return (b.data.players?.length || 0) - (a.data.players?.length || 0);
      return 0;
    });

    return list;
  }, [archive, search, sortBy, typeFilter, statusFilter]);

  // Reset to page 1 when filter/search/sort/page-size changes
  useEffect(() => {
    setPage(1);
  }, [search, sortBy, typeFilter, statusFilter, pageSize]);

  // Pagination: show only the current page (max display count)
  const pageCount = Math.max(1, Math.ceil(filteredArchive.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * pageSize;
  const pagedArchive = filteredArchive.slice(pageStart, pageStart + pageSize);

  const toggleOne = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filteredArchive.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredArchive.map((item) => item.key)));
    }
  };

  const handleBulkDelete = () => {
    selected.forEach((key) => onDeleteArchived(key));
    setSelected(new Set());
    setConfirmDelete(null);
  };

  const handleSingleDelete = (key) => {
    onDeleteArchived(key);
    setSelected((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    setConfirmDelete(null);
  };

  const allSelected = filteredArchive.length > 0 && selected.size === filteredArchive.length;
  const someSelected = selected.size > 0 && selected.size < filteredArchive.length;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 slide-up">
      <button onClick={onBack} className="btn-ghost mb-4 -ml-2">
        <ArrowLeft size={14} /> Hjem
      </button>

      <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-semibold mb-1">Turneringsarkiv</h1>
          <p className="text-ink-2 text-sm">
            {viewMode === 'activities'
              ? 'Alle turneringer og enkeltkamper. Klikk på en for å åpne, eller velg flere for å slette samtidig.'
              : 'Alle spilte kamper på tvers av turneringer og enkeltkamper, sortert nyeste først.'}
          </p>
        </div>
        {viewMode === 'activities' && (
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => handleExport(false)}
              className="btn-outline text-xs"
              title="Last ned alle turneringer som backup-fil"
            >
              <Save size={12} /> Eksporter alle
            </button>
            {/* The file input was `display: none`, so «Importer backup» could
                not be reached from a keyboard (#900). Visually hidden instead,
                still inside its label, which draws the focus ring for it. */}
            <label
              className="btn-outline text-xs cursor-pointer has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:[outline-color:var(--focus-ring)]"
              title="Last opp backup-fil for å gjenopprette turneringer"
            >
              <input
                type="file"
                accept="application/json,.json"
                onChange={handleImport}
                className="sr-only"
              />
              <Plus size={12} /> Importer backup
            </label>
          </div>
        )}
      </div>

      {/* Paused tournaments — at the top of the archive */}
      {pausedTournaments.length > 0 && (
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-3">
            <Pause size={14} style={{ color: '#a16207' }} />
            <h2 className="font-display text-base font-semibold" style={{ color: '#a16207' }}>
              På pause
            </h2>
            <span className="font-mono text-[10px] text-ink-3">{pausedTournaments.length}</span>
          </div>
          <div className="space-y-2">
            {pausedTournaments.map((t) => {
              const tt = getTournamentType(t.config);
              const completedCount = (t.matches || []).filter((m) => m.completed).length;
              const totalCount = (t.matches || []).length;
              return (
                <div
                  key={t.id}
                  className="card-elev p-3 flex items-center gap-3 flex-wrap border-l-4"
                  style={{
                    borderLeftColor: '#eab308',
                    background: 'linear-gradient(135deg, #fefce8 0%, #fef9c3 100%)',
                  }}
                >
                  <span
                    className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0"
                    style={{ background: tt.color + '40', color: '#0f1611' }}
                  >
                    {tt.abbrev}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-sm font-semibold truncate">
                      {t.config?.name || 'Turnering'}
                    </div>
                    <div className="text-[11px] text-ink-3 font-mono">
                      {t.players?.length || 0} spillere ·{' '}
                      {totalCount > 0
                        ? `${completedCount}/${totalCount} kamper spilt`
                        : 'ikke startet'}
                      {(t.type === 'americano' ||
                        t.type === 'mexicano' ||
                        t.type === 'mixicano' ||
                        t.type === 'team_mexicano' ||
                        t.type === 'mix_americano' ||
                        t.type === 'club_americano' ||
                        t.type === 'club_team_americano' ||
                        t.type === 'club_mexicano') &&
                        t.currentRound > 0 &&
                        ` · Runde ${t.currentRound}`}
                    </div>
                    {t.id && (
                      <div className="font-mono text-[10px] text-ink-3 mt-0.5 truncate">
                        ID:{' '}
                        <span className="text-ink-2 select-all font-semibold">
                          {getDisplayTournamentId(t)}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    {onResumePaused && (
                      <button onClick={() => onResumePaused(t.id)} className="btn-lime text-xs">
                        <Play size={11} /> Gjenoppta
                      </button>
                    )}
                    {onDeletePaused && (
                      <ConfirmButton
                        onConfirm={() => onDeletePaused(t.id)}
                        question={`Slett pauset turnering "${t.config?.name || 'Turnering'}"? All data går tapt.`}
                        confirmLabel="Slett"
                        className="btn-ghost text-xs text-rose hover:bg-rose-soft"
                        title="Slett pauset turnering"
                      >
                        <Trash2 size={11} />
                      </ConfirmButton>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tab toggle: Turneringer / Alle kamper */}
      <div className="mb-4 flex gap-1 border-b border-line">
        <button
          onClick={() => setViewMode('activities')}
          className={`px-4 py-2 text-sm font-semibold transition-colors border-b-2 -mb-px ${
            viewMode === 'activities'
              ? 'border-ink text-ink'
              : 'border-transparent text-ink-3 hover:text-ink-2'
          }`}
        >
          <Layers size={14} className="inline mr-1.5 -mt-0.5" />
          Turneringer
        </button>
        <button
          onClick={() => setViewMode('matches')}
          className={`px-4 py-2 text-sm font-semibold transition-colors border-b-2 -mb-px ${
            viewMode === 'matches'
              ? 'border-ink text-ink'
              : 'border-transparent text-ink-3 hover:text-ink-2'
          }`}
        >
          <ListChecks size={14} className="inline mr-1.5 -mt-0.5" />
          Alle kamper
        </button>
      </div>

      {/* Status message for import/export */}
      {importStatus && (
        <div
          className="mb-3 px-4 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2"
          style={{
            background: importStatus === 'success' ? '#dcfce7' : '#fee2e2',
            color: importStatus === 'success' ? '#166534' : '#9f1239',
            border: `1px solid ${importStatus === 'success' ? '#86efac' : '#fca5a5'}`,
          }}
        >
          {importStatus === 'success' ? <Check size={14} /> : <AlertCircle size={14} />}
          {importMessage}
        </div>
      )}

      {/* RENDER: activities mode (existing UI) */}
      {viewMode === 'activities' && (
        <>
          {/* Filter row */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <div className="relative" style={{ flex: '1 1 100px', minWidth: 0 }}>
              <Search
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-3 pointer-events-none"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Søk..."
                aria-label={tr('archive.searchLabel')}
                className="input w-full text-sm"
                style={{ paddingLeft: '2rem' }}
              />
            </div>

            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="input text-xs py-1.5 pr-7"
              style={{ flex: '0 0 auto', width: 'auto' }}
              title="Filtrer på type"
              aria-label="Filtrer på type"
            >
              {availableTypes.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>

            {availableStatuses.length > 2 && (
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="input text-xs py-1.5 pr-7"
                style={{ flex: '0 0 auto', width: 'auto' }}
                title="Filtrer på status"
                aria-label="Filtrer på status"
              >
                {availableStatuses.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
              </select>
            )}

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="input text-xs py-1.5 pr-7"
              style={{ flex: '0 0 auto', width: 'auto' }}
              title="Sortering"
              aria-label="Sortering"
            >
              <option value="newest">Nyeste</option>
              <option value="oldest">Eldste</option>
              <option value="name">Navn A–Å</option>
              <option value="players">Flest spillere</option>
            </select>

            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="input text-xs py-1.5 pr-7"
              style={{ flex: '0 0 auto', width: 'auto' }}
              title="Antall per side"
              aria-label="Antall per side"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={250}>250</option>
            </select>
          </div>

          {/* Bulk actions */}
          {filteredArchive.length > 0 && (
            <div className="card p-3 mb-3 flex items-center gap-3 flex-wrap">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected;
                  }}
                  onChange={toggleAll}
                  className="w-4 h-4 cursor-pointer"
                />
                <span className="text-xs font-mono uppercase tracking-wider text-ink-3 font-semibold">
                  {selected.size === 0
                    ? 'Velg alle'
                    : selected.size === filteredArchive.length
                      ? 'Alle valgt'
                      : `${selected.size} valgt`}
                </span>
              </label>

              {selected.size > 0 && (
                <>
                  <span className="text-ink-4">·</span>
                  <button
                    onClick={() => handleExport(true)}
                    className="text-xs font-semibold text-ink-2 hover:underline flex items-center gap-1"
                  >
                    <Save size={12} /> Eksporter {selected.size} valgte
                  </button>
                  <button
                    onClick={() => setConfirmDelete('bulk')}
                    className="text-xs font-semibold text-rose hover:underline flex items-center gap-1"
                  >
                    <Trash2 size={12} /> Slett {selected.size} valgte
                  </button>
                  <button
                    onClick={() => setSelected(new Set())}
                    className="text-xs text-ink-3 hover:text-ink-2"
                  >
                    Avbryt valg
                  </button>
                </>
              )}

              <span className="ml-auto text-xs text-ink-3">
                {filteredArchive.length === 0
                  ? '0'
                  : `${pageStart + 1}–${Math.min(pageStart + pageSize, filteredArchive.length)}`}{' '}
                av {filteredArchive.length} turnering{filteredArchive.length === 1 ? '' : 'er'}
              </span>
            </div>
          )}

          {/* Tournament list */}
          {filteredArchive.length === 0 ? (
            <div className="card p-12 text-center text-ink-3">
              <Trophy size={32} className="mx-auto mb-3 opacity-50" />
              {archive.length === 0 ? (
                <>
                  <div className="font-display text-lg mb-1">Ingen turneringer arkivert ennå</div>
                  <div className="text-sm">
                    Når du fullfører eller lagrer en turnering vises den her
                  </div>
                </>
              ) : (
                <>
                  <div className="font-display text-lg mb-1">Ingen treff</div>
                  <div className="text-sm">Prøv et annet søk</div>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {pagedArchive.map((item) => {
                const t = item.data;
                // Get ALL winners — can be 2 (the winning pair in the final for
                // both Padel24 and Americano) or more when 1st place is shared
                // (Americano with winnerSource='standings'). Falls back to legacy
                // finalRanking[0] for old data.
                const winners = getTournamentWinners(t);
                const date = t.archivedAt ? new Date(t.archivedAt) : null;
                const dateLabel = date
                  ? date.toLocaleDateString('no-NO', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })
                  : 'Ukjent dato';
                const playerCount = t.players?.length || 0;
                const matchCount = t.matches?.length || 0;
                const completedCount = t.matches?.filter((m) => m.completed).length || 0;
                const isSelected = selected.has(item.key);

                // Type info from the engine (consistent with the rest of the app)
                const typeInfo = getTournamentType(t.config);

                return (
                  <div
                    key={item.key}
                    className={`card-elev p-4 transition-all ${isSelected ? 'ring-2 ring-ink' : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      {!t.isVirtual && (
                        <input
                          type="checkbox"
                          aria-label={tr('archive.selectRow', {
                            name: t.config?.name || 'Turnering',
                          })}
                          checked={isSelected}
                          onChange={() => toggleOne(item.key)}
                          className="w-4 h-4 cursor-pointer flex-shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      {t.isVirtual && <div className="w-4 flex-shrink-0" />}

                      <div
                        onClick={() => onOpenArchived(t)}
                        className="flex-1 min-w-0 cursor-pointer"
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') onOpenArchived(t);
                        }}
                      >
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span
                            className="font-mono text-[10px] font-bold px-2 py-0.5 rounded"
                            style={{ background: typeInfo.color + '40', color: '#0f1611' }}
                            title={typeInfo.label}
                          >
                            {typeInfo.label}
                          </span>
                          {!t.isVirtual && (
                            <span className="pill bg-bg-soft text-ink-2 text-[10px]">
                              <Trophy size={10} className="inline mr-1" />
                              {t.status === 'done' || t.isSeriesSeason ? 'Ferdig' : 'Lagret'}
                            </span>
                          )}
                          <span className="font-mono text-[10px] text-ink-3">{dateLabel}</span>
                          <span className="font-mono text-[10px] text-ink-4">·</span>
                          <span className="font-mono text-[10px] text-ink-3">
                            {t.isVirtual
                              ? `${matchCount} kamper`
                              : `${completedCount}/${matchCount} kamper`}
                          </span>
                        </div>
                        <div className="font-display text-base font-semibold leading-tight mb-0.5 truncate">
                          {highlightMatch(t.config?.name || 'Turnering', search)}
                        </div>
                        <div className="text-xs text-ink-3 truncate">
                          {playerCount} spillere
                          {winners && winners.winners.length > 0 && (
                            <> · 🏆 {winners.winners.map((p) => p.name).join(' & ')}</>
                          )}
                          {winners && winners.runnersUp && winners.runnersUp.length > 0 && (
                            <span className="text-ink-4">
                              {' '}
                              · 🥈 {winners.runnersUp.map((p) => p.name).join(' & ')}
                            </span>
                          )}
                        </div>
                        {!t.isVirtual && (t.id || t.archiveId) && (
                          <div
                            className="font-mono text-[10px] text-ink-3 mt-1 truncate"
                            title="Turnerings-ID — vises også på hver kamp i denne turneringen"
                          >
                            ID:{' '}
                            <span className="text-ink-2 select-all font-semibold">
                              {getDisplayTournamentId(t)}
                            </span>
                          </div>
                        )}
                      </div>

                      {!t.isVirtual && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmDelete(`single:${item.key}`);
                          }}
                          className="flex-shrink-0 p-2 rounded-full text-ink-3 hover:bg-rose-soft hover:text-rose transition-colors"
                          title="Slett turnering"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {pageCount > 1 && (
            <div className="flex items-center justify-center gap-3 mt-4">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={safePage <= 1}
                className="text-sm font-semibold px-3 py-1.5 rounded-full border border-line flex items-center gap-1"
                style={{
                  opacity: safePage <= 1 ? 0.4 : 1,
                  cursor: safePage <= 1 ? 'not-allowed' : 'pointer',
                }}
              >
                <ChevronRight size={14} style={{ transform: 'rotate(180deg)' }} /> Forrige
              </button>
              <span className="text-xs text-ink-2 font-mono px-1">
                Side {safePage} av {pageCount}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                disabled={safePage >= pageCount}
                className="text-sm font-semibold px-3 py-1.5 rounded-full border border-line flex items-center gap-1"
                style={{
                  opacity: safePage >= pageCount ? 0.4 : 1,
                  cursor: safePage >= pageCount ? 'not-allowed' : 'pointer',
                }}
              >
                Neste <ChevronRight size={14} />
              </button>
            </div>
          )}
        </>
      )}
      {viewMode === 'matches' && (
        <AllMatchesArchive
          archive={archive}
          effectivePlayerDB={effectivePlayerDB}
          onOpenArchived={onOpenArchived}
          onPlayerClick={onPlayerClick}
        />
      )}

      {/* Confirm modal for deletion — a `Dialog` (#900): named by its question,
          described by its consequence. Escape is «Avbryt», as the backdrop is,
          and focus goes back to the delete button that asked. */}
      {confirmDelete && (
        <Dialog
          labelledBy={confirmIds.title}
          describedBy={confirmIds.body}
          onClose={() => setConfirmDelete(null)}
          showClose={false}
          initialFocus={cancelDeleteRef}
          backdropClassName=""
          backdropStyle={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            background: 'rgba(15, 22, 17, 0.55)',
          }}
          className="overflow-y-auto max-h-[92vh]"
          style={{
            maxHeight: 'calc(100dvh - 2rem)',
            background: '#ffffff',
            maxWidth: 440,
            width: '100%',
            borderRadius: 16,
            padding: 20,
            boxShadow: '0 25px 60px -10px rgba(0,0,0,0.4)',
            border: '1px solid #e7eae3',
          }}
        >
          {confirmDelete === 'bulk' ? (
            <>
              <h3 id={confirmIds.title} className="font-display text-lg font-semibold mb-1">
                Slette {selected.size} turneringer?
              </h3>
              <p id={confirmIds.body} className="text-sm text-ink-2 mb-4 leading-relaxed">
                Du er i ferd med å slette <strong>{selected.size}</strong> turneringer permanent.
                Alle data vil gå tapt og kan ikke gjenopprettes.
              </p>
              <div className="flex gap-2 justify-end">
                <button
                  ref={cancelDeleteRef}
                  type="button"
                  onClick={() => setConfirmDelete(null)}
                  className="btn-ghost"
                >
                  Avbryt
                </button>
                <button
                  type="button"
                  onClick={handleBulkDelete}
                  className="px-4 py-2 rounded-full text-sm font-semibold text-white hover:opacity-90"
                  style={{ background: '#f43f5e' }}
                >
                  Ja, slett {selected.size}
                </button>
              </div>
            </>
          ) : (
            <>
              <h3 id={confirmIds.title} className="font-display text-lg font-semibold mb-1">
                Slette turnering?
              </h3>
              <p id={confirmIds.body} className="text-sm text-ink-2 mb-4 leading-relaxed">
                Er du sikker på at du vil slette{' '}
                <strong>
                  {archive.find((it) => it.key === confirmDelete.slice(7))?.data?.config?.name ||
                    'denne turneringen'}
                </strong>
                ? Alle data vil bli slettet permanent og kan ikke gjenopprettes.
              </p>
              <div className="flex gap-2 justify-end">
                <button
                  ref={cancelDeleteRef}
                  type="button"
                  onClick={() => setConfirmDelete(null)}
                  className="btn-ghost"
                >
                  Avbryt
                </button>
                <button
                  type="button"
                  onClick={() => handleSingleDelete(confirmDelete.slice(7))}
                  className="px-4 py-2 rounded-full text-sm font-semibold text-white hover:opacity-90"
                  style={{ background: '#f43f5e' }}
                >
                  Ja, slett permanent
                </button>
              </div>
            </>
          )}
        </Dialog>
      )}
    </div>
  );
}
